import type { AppConfig, BrandPaletteColor } from './types';

// Sirktek brand tokens — kept in sync with sirk-asset-frontend so the
// orgadmin <-> asset hop reads as one product. Source of truth there.
const PURPLE: BrandPaletteColor = {
    main: '#8155ca',
    light: '#b08ee0',
    dark: '#6a3fb0',
    contrastText: '#ffffff',
};

// Dark mode needs a lighter tint: #8155ca is only ~3.5:1 on the #121212 paper.
// The tint passes 4.5:1 as text on both dark surfaces and, with the near-black
// contrastText, as a button fill.
const PURPLE_DARK: BrandPaletteColor = {
    main: '#b08ee0',
    light: '#c9b0ec',
    dark: '#9a74d6',
    contrastText: '#1a1a1a',
};

const LAVENDER: BrandPaletteColor = {
    main: '#e6d9f7',
    light: '#f5f1fb',
    dark: '#c5b3e0',
    contrastText: '#1a1a1a',
};

const MONTSERRAT_HEADING = '"Montserrat", sans-serif';
const SYSTEM_BODY =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Helvetica Neue", sans-serif';

export const sirktekApp: AppConfig = {
    id: 'sirktek',
    productName: 'Sirktek',
    htmlTitle: 'Admin - Sirktek',
    appLinkUrl: 'https://www.sirktek.com',
    homepageUrl: 'https://sirktek.no',
    showBackToApp: true,
    logo: { src: '/apps/sirktek/logo.svg', alt: 'Sirktek' },
    favicon: '/apps/sirktek/logo.svg',
    brand: {
        light: {
            primary: PURPLE,
            secondary: LAVENDER,
            background: { default: '#f5f1fb', paper: '#ffffff' },
        },
        dark: {
            primary: PURPLE_DARK,
            secondary: LAVENDER,
            // React Admin's / MUI's dark surfaces, spelled out so the dark theme
            // is derived from tokens like every other app rather than inherited.
            background: { default: '#313131', paper: '#121212' },
        },
        fonts: { heading: MONTSERRAT_HEADING, body: SYSTEM_BODY },
        heroGradient: 'linear-gradient(225deg, #8155ca 0%, #e6d9f7 15%, #f5f1fb 100%)',
    },
    defaultThemeMode: 'light',
    enabledResources: new Set([
        'settings',
        'billing',
        'user',
        'api-key',
        'audit-event',
        'access-grant',
        'product',
        'service',
        'category/location',
        'category/furniture',
        'category/model',
        'category/manufacturer',
        'category/machine',
        'category/realestate',
        'category/controlpoint',
        'category/ict',
    ]),
    signup: {
        // Two rungs, not the three this app shipped with: PRODUCT_PASSPORT and
        // SERVICE_ORDERS are retired (sirk-orgadmin-backend#153). The product and
        // service permissions they used to sell moved to the reinventar and vardly
        // ladders, and the reporting rung with them — what is left on the sirktek
        // ladder is asset management, and API keys / MCP above it.
        //
        // Both tiers are now metered per step of 100 active or stored objects, the
        // same packaging shape vardly uses at 50: the next 100 objects cost the tier
        // price again. Display only — the backend does not meter steps yet.
        tiers: [
            {
                id: 'ASSET_MANAGEMENT',
                i18nKey: 'asset',
                prices: { NOK: 750 },
                featureCount: 4,
                highlighted: false,
                metered: { objects: 100 },
            },
            {
                // API keys, and with them the MCP/AI surface those keys
                // authenticate. Sold on SIRKTEK rank 2 (Permission.READ/
                // WRITE_API_KEYS), which is why the base tier stays keyless.
                id: 'API_AI',
                i18nKey: 'apiAi',
                prices: { NOK: 1250 },
                featureCount: 3,
                highlighted: true,
                metered: { objects: 100 },
            },
            {
                // Sold by agreement: no price, a contact link instead. Same
                // permissions as API_AI; SSO is set up manually with us.
                id: 'SIRKTEK_ENTERPRISE',
                i18nKey: 'sirktekEnterprise',
                contactEmail: 'kontakt@sirktek.no',
                featureCount: 6,
                highlighted: false,
            },
        ],
        // Norway only. Matches `countries.app.sirktek` in sirk-orgadmin-backend,
        // which is the enforcing gate; this list decides what the picker offers.
        countries: ['no'],
    },
    defaultTaxonomies: ['furniture'],
};
